import { RentPayment, Property, Tenant } from '../../types/properties';
import { formatCurrency } from '../../lib/format';
import { format } from 'date-fns';
import { Download, CheckCircle2 } from 'lucide-react';

interface RentReceiptProps {
  payment: RentPayment;
  property: Property;
  tenant?: Tenant;
  onClose: () => void;
}

export function RentReceipt({ payment, property, tenant, onClose }: RentReceiptProps) {

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="flex flex-col gap-6 p-6">

      <div id="receipt-content" className="bg-white text-gray-900 p-8 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">
        {/* Receipt Header */}
        <div className="flex justify-between items-start mb-8 pb-6 border-b border-gray-200">
          <div>
            <h1 className="text-2xl font-black text-gray-900 uppercase tracking-widest mb-1">Receipt</h1>
            <p className="text-sm text-gray-500 font-medium">No. {payment.id.split('-')[0].toUpperCase()}</p>
          </div>
          <div className="text-right">
            <h2 className="text-lg font-bold text-gray-900">WholeScale OS Properties</h2>
            <p className="text-sm text-gray-500">{format(new Date(), 'MMMM d, yyyy')}</p>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Received From</h3>
            <p className="text-base font-medium text-gray-900">{tenant?.name || 'Unknown Tenant'}</p>
            {tenant?.email && <p className="text-sm text-gray-500">{tenant.email}</p>}
          </div>
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">For Property</h3>
            <p className="text-base font-medium text-gray-900">{property.address}</p>
            <p className="text-sm text-gray-500">{property.city}, {property.state} {property.zip}</p>
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-100">
          <div className="flex justify-between items-end mb-6 pb-6 border-b border-gray-200">
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Amount Received</h3>
              <div className="text-3xl font-black text-gray-900">{formatCurrency(payment.amount)}</div>
            </div>
            <div className="flex items-center gap-2 text-green-600 bg-green-100 px-3 py-1.5 rounded-lg font-bold text-sm uppercase tracking-wider">
              <CheckCircle2 size={16} /> Paid
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Payment Date</p>
              <p className="text-sm font-medium text-gray-900">{format(new Date(payment.payment_date), 'MMM d, yyyy')}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Payment Method</p>
              <p className="text-sm font-medium text-gray-900 capitalize">{payment.payment_method?.replace('-', ' ') || 'Unknown'}</p>
            </div>
            {payment.notes && (
              <div className="col-span-2 mt-2">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Notes</p>
                <p className="text-sm font-medium text-gray-900">{payment.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 pt-6 border-t border-gray-200 font-medium">
          Thank you for your payment. Keep this receipt for your records.
        </div>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-[var(--t-border)]">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[var(--t-text-muted)] hover:text-white transition-colors">Close</button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-6 py-2 bg-[var(--t-primary)] text-white text-sm font-bold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)]"
        >
          <Download size={16} /> Print / Save PDF
        </button>
      </div>

    </div>
  );
}
